import { apiFetch, setToken, clearToken, ApiError } from './client'

export interface UserPermissionsDto {
  admin_general?: boolean
  admin_user_management?: boolean
  warehouse_general?: boolean
  warehouse_book?: boolean
  warehouse_count?: boolean
  warehouse_lead?: boolean
  warehouse_admin?: boolean
  // uebrige Flags existieren, werden im Frontend derzeit nicht einzeln benutzt

  [key: string]: boolean | undefined
}

export interface UserProfile {
  id: number
  email: string
  name: string
  permissions: UserPermissionsDto
}

export interface LoginResponse {
  token: string
  user: UserProfile
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  // Backend gibt session_token direkt im ACK-Envelope zurück (nicht in data:{})
  const res = await fetch('/api/auth/login/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const body = (await res.json()) as {
    ack: 0 | 1
    session_token?: string
    user?: UserProfile
    error?: Array<{ code: number; message: string }>
  }
  if (!res.ok || body.ack === 0) {
    throw new ApiError(res.status, 'AUTH_ERROR', body.error?.[0]?.message ?? 'Login fehlgeschlagen')
  }
  const token = body.session_token!
  const user = body.user!
  setToken(token)
  localStorage.setItem('stahlia_user', JSON.stringify(user))
  return { token, user }
}

export async function logout(): Promise<void> {
  try {
    await apiFetch('/auth/logout/', { method: 'POST' })
  } finally {
    clearToken()
  }
}

/** Gesperrter Browser-Speicher (privater Modus, Sandbox) gilt als „nicht angemeldet“ statt den Baum zu reißen. */
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function getCurrentUser(): UserProfile | null {
  const raw = readStorage('stahlia_user')
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserProfile
  } catch {
    return null
  }
}

// React Hook für Auth-Status
export function useAuth() {
  const user = getCurrentUser()
  const p = user?.permissions ?? {}
  const wh = {
    general: !!(p.warehouse_general || p.warehouse_book || p.warehouse_count || p.warehouse_lead || p.warehouse_admin),
    book: !!(p.warehouse_book || p.warehouse_admin),
    count: !!(p.warehouse_count || p.warehouse_admin),
    lead: !!(p.warehouse_lead || p.warehouse_admin),
    admin: !!p.warehouse_admin,
  }
  return {
    isLoggedIn: !!readStorage('stahlia_token'),
    user,
    // Fix: Backend-Flag heisst admin_general (vorher permissions.admin → immer false,
    // JEDER Admin bekam "Kein Zugriff" — Live-Diagnose 2026-07-09)
    isAdmin: !!p.admin_general,
    warehouse: wh,
    // Lager-Mitarbeiter-Modus: NUR warehouse-Flags gesetzt → Nav zeigt nur Lager (Spec §6)
    isWarehouseOnly: wh.general && !p.admin_general && !p.order_general && !p.article_general,
  }
}
